import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import Icon from "./Icon";
import MobileDrawer from "./MobileDrawer";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/manufacturing", label: "Manufacturing" },
  { to: "/quality", label: "Quality" },
  { to: "/markets", label: "Markets" },
  { to: "/contact", label: "Contact" },
];

const LOGO = "/logo.webp";

const SCROLLED_THRESHOLD = 80;
const HIDE_START = 200;
const HIDE_DELTA = 6;
const SHOW_DELTA = -4;
const IDLE_REVEAL_MS = 1600;

/**
 * Header — solid at top, glass + shrink when scrolled, hides on scroll-down,
 * reappears on scroll-up. Reveals after idle.
 */
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastY = useRef(0);
  const idleTimer = useRef(null);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleScroll = useCallback(() => {
    const y = window.scrollY;
    setScrolled(y > SCROLLED_THRESHOLD);
    const delta = y - lastY.current;
    if (y > HIDE_START && delta > HIDE_DELTA) setHidden(true);
    else if (delta < SHOW_DELTA) setHidden(false);
    lastY.current = y;

    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setHidden(false), IDLE_REVEAL_MS);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const toggleMobile = useCallback(() => setMobileOpen((o) => !o), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return (
    <>
      <header
        className={`site-header ${scrolled ? "is-scrolled" : ""} ${hidden ? "is-hidden" : ""}`}
        data-testid="site-header"
      >
        <div className="container-x site-header__inner">
          <Link to="/" className="wordmark" aria-label="Kunova Impex — home" data-testid="header-logo">
            <img
              src={LOGO}
              alt="Kunova Impex — Towel Manufacturers & Global Exporters"
              className="wordmark__img"
              style={{ borderRadius: 12 }}
            />
          </Link>

          <div className="site-header__actions">
            <DesktopNav />
            <Link
              to="/contact#quote"
              className="btn btn--primary btn--sm hidden-mobile"
              data-testid="header-cta-quote-desktop"
            >
              Get Quote
            </Link>
            <button
              className="mobile-toggle"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={toggleMobile}
              data-testid="mobile-menu-toggle"
            >
              <Icon name={mobileOpen ? "close" : "menu"} />
            </button>
          </div>
        </div>
      </header>

      <MobileDrawer open={mobileOpen} nav={NAV} onClose={closeMobile} />

      <style>{`
        .hidden-mobile { display: none; }
        @media (min-width: 768px) { .hidden-mobile { display: inline-flex; } }
      `}</style>
    </>
  );
}

/** Primary desktop navigation links. */
function DesktopNav() {
  return (
    <nav aria-label="Primary">
      <ul className="nav-links">
        {NAV.map((n) => (
          <li key={n.to}>
            <NavLink
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) => (isActive ? "is-active" : "")}
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
